import { Children, ReactNode } from 'react'
import { AlertTriangle, Inbox, Lock } from 'lucide-react'
import Spinner from '@/common/components/Spinner'
import { cn } from '@/common/utils/cn'

type Props = {
  title: string
  count: number
  children: ReactNode
  accentClassName?: string
  className?: string
  isLoading?: boolean
  isError?: boolean
  forbidden?: boolean
}

const ColumnMessage = ({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: string }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
    <span className="text-white-300">{icon}</span>
    <p className="text-xs font-semibold text-white-400">{title}</p>
    {subtitle && <p className="text-[11px] text-white-300">{subtitle}</p>}
  </div>
)

const ProposalKanbanColumn = ({
  title,
  count,
  children,
  accentClassName,
  className,
  isLoading,
  isError,
  forbidden,
}: Props) => {
  const isEmpty = Children.count(children) === 0

  const renderBody = () => {
    if (forbidden) {
      return (
        <ColumnMessage
          icon={<Lock size={28} strokeWidth={1.5} />}
          title="Sem permissão"
          subtitle="Você não tem permissão para ver estas propostas."
        />
      )
    }

    if (isError) {
      return (
        <ColumnMessage
          icon={<AlertTriangle size={28} strokeWidth={1.5} />}
          title="Erro ao carregar"
          subtitle="Não foi possível carregar as propostas."
        />
      )
    }

    if (isLoading && isEmpty) {
      return (
        <div className="flex flex-1 items-center justify-center py-10">
          <Spinner className="h-6 w-6 fill-blue-800" />
        </div>
      )
    }

    if (isEmpty) {
      return (
        <ColumnMessage
          icon={<Inbox size={28} strokeWidth={1.5} />}
          title="Nenhuma proposta"
          subtitle="Nada por aqui no momento."
        />
      )
    }

    return children
  }

  return (
    <div
      className={cn(
        'flex flex-1 min-w-[280px] border border-border-default rounded-lg overflow-hidden',
        className,
      )}
    >
      {accentClassName && <div className={cn('w-[3px] shrink-0', accentClassName)} />}

      <div className="flex flex-col flex-1 min-w-0">
        <div className="px-4 py-3 bg-white-100 border-b border-border-default shrink-0 flex items-center justify-between gap-2">
          <p className="font-bold text-sm">
            {title} <span className="text-yellow-400">({count})</span>
          </p>
        </div>

        <div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1 no-scrollbar bg-black-300">
          {renderBody()}
        </div>
      </div>
    </div>
  )
}

export default ProposalKanbanColumn
